import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  TextField,
} from '@mui/material';
import './App.css';
import reportWerksLogo from './assets/reportwerks-logo.png';
import reportingTeam from './assets/reporting-team.webp';
import heroReportingConfidence from './assets/reportwerks-hero-reporting-confidence.jpg';
import auditCover from './assets/marketing-data-confidence-audit-cover.png';
import { trackGa4Event, trackLeadFormSubmission } from './analytics';
import { privacyPolicyContent, termsOfServiceContent } from './legalContent';
import MarketingDataAuditTool from './MarketingDataAuditTool';
import { auditLeadEndpoint, auditToolPath, grantAuditAccess } from './auditLeadAccess';
import AuditDownloadButton from './AuditDownloadButton';
import { integrationCategories, integrationCount, integrations } from './integrationsData';
import { submitMauticLead } from './leadSubmission';
import ComparisonPage, { ComparisonTeaser } from './ComparisonPage';
import { comparisonPath, comparisonMetadata, comparisonFaqs } from './comparisonData';

const appointmentEndpoint =
  process.env.REACT_APP_APPOINTMENT_ENDPOINT || 'https://api.digitalwerks.com/mautic/lead';
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const canonicalBaseUrl = 'https://www.reportwerks.com';
const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'support@reportwerks.com';
const supportEmailHref = `mailto:${supportEmail}`;
const successMessage = 'Thanks. Donnie will follow up shortly.';
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const auditSegmentId = 31;
const auditResponderId = 80;
const auditNotificationId = 81;

const stripeCheckoutLinks = {
  essentials: {
    monthly: 'https://buy.stripe.com/5kQ5kE2wrfGO1uw5AKgA800',
    annual: 'https://buy.stripe.com/3cIdRa4Ez7ai3CE0gqgA801',
  },
  professional: {
    monthly: 'https://buy.stripe.com/14AcN61sn1PY5KM2oygA802',
    annual: 'https://buy.stripe.com/28E28s3Av9iq1uw6EOgA803',
  },
  growth: {
    monthly: 'https://buy.stripe.com/eVq14o1sn52a3CE3sCgA804',
    annual: 'https://buy.stripe.com/eVq7sM4EzgKS6OQ9R0gA805',
  },
};

const homeNavigation = [
  { label: 'How it works', href: '#product' },
  { label: 'For teams', href: '#for-teams' },
  { label: 'Confidence', href: '#confidence' },
  { label: 'Integrations', href: '/integrations' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Data & privacy', href: '#data-usage' },
  { label: 'Resources', href: 'https://blog.reportwerks.com', external: true },
];

const initialFormState = {
  name: '',
  email: '',
  organization: '',
  website: '',
  role: '',
  organization_type: '',
  team_size: '',
  phone: '',
  preferred_window: '',
  timeline: '',
  reporting_stack: '',
  biggest_challenge: '',
  appointment_goal: '',
  consent: false,
  emailid: 27,
  responder: 26,
  segment: 26,
  emailContactId: 190789,
  domain: hostname,
};

const bookingFormId = 'reportwerks-booking-form';
const bookingFormName = 'Request your appointment';

const getCurrentPage = () => {
  if (typeof window === 'undefined') return 'home';
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return 'home';
  if (path === '/book') return 'book';
  if (path === '/integrations') return 'integrations';
  if (path === '/pricing') return 'pricing';
  if (path === comparisonPath) return 'comparison';
  if (path === '/privacy-policy') return 'privacy';
  if (path === '/terms-of-service') return 'terms';
  if (path === '/marketing-data-confidence-audit') return 'audit';
  if (path === '/marketing-data-confidence-audit-tool') return 'audit-tool';
  return 'not-found';
};

const buildBookingLeadTrackingContext = () => ({
  form_id: bookingFormId,
  form_name: bookingFormName,
  form_location: typeof window !== 'undefined' ? window.location.pathname || '/book' : '/book',
  lead_type: 'walkthrough_request',
  page_location:
    typeof window !== 'undefined' ? window.location.href : `${canonicalBaseUrl}/book`,
  page_title:
    typeof document !== 'undefined' ? document.title : 'Book a ReportWerks Walkthrough',
  source_component: 'appointment_form',
});

function ArrowIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20">
      <path d="M4 10h11M11 6l4 4-4 4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20">
      <path d="m4 10 4 4 8-8" />
    </svg>
  );
}

function Logo({ inverse = false }) {
  return (
    <a className={`logo ${inverse ? 'logo--inverse' : ''}`} href="/" aria-label="ReportWerks home">
      <img src={reportWerksLogo} alt="ReportWerks" />
    </a>
  );
}

function Header({ simple = false }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const items = simple
    ? [
        { label: 'Overview', href: '/' },
        { label: 'Integrations', href: '/integrations' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'Resources', href: 'https://blog.reportwerks.com', external: true },
        { label: 'Privacy', href: '/privacy-policy' },
        { label: 'Terms', href: '/terms-of-service' },
      ]
    : homeNavigation;

  return (
    <header className="site-header">
      <div className="shell nav-shell">
        <Logo />
        <button
          className="nav-toggle"
          type="button"
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((value) => !value)}
        >
          <span />
          <span />
        </button>
        <nav className={`nav-links ${menuOpen ? 'nav-links--open' : ''}`} aria-label="Main navigation">
          {items.map((item) => (
            <a
              key={item.label}
              href={item.href}
              target={item.external ? '_blank' : undefined}
              rel={item.external ? 'noreferrer' : undefined}
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
              {item.external ? <span aria-hidden="true"> ↗</span> : null}
            </a>
          ))}
          <a className="nav-login" href="https://app.reportwerks.com">
            Log in
          </a>
          <a className="button button--small" href="/book">
            Book a walkthrough
          </a>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer" itemScope itemType="https://schema.org/Organization">
      <meta itemProp="name" content="ReportWerks" />
      <meta itemProp="url" content={`${canonicalBaseUrl}/`} />
      <div className="shell footer-grid">
        <div>
          <Logo inverse />
          <p className="footer-note">
            Clear, defensible reporting for teams accountable to clients, boards, funders, and leadership.
          </p>
        </div>
        <div className="footer-links" aria-label="Footer links">
          <a href="/book">Book a walkthrough</a>
          <a href="/integrations">Explore integrations</a>
          <a href="/pricing">View pricing</a>
          <a href={comparisonPath}>Compare Looker Studio</a>
          <a href="https://blog.reportwerks.com">Visit the blog</a>
          <a href="/privacy-policy">Privacy Policy</a>
          <a href="/terms-of-service">Terms of Service</a>
          <a href={supportEmailHref} itemProp="email">{supportEmail}</a>
        </div>
      </div>
      <div className="shell footer-bottom">
        <span>© {new Date().getFullYear()} ReportWerks</span>
        <span>Reporting you can stand behind.</span>
      </div>
    </footer>
  );
}

function SectionHeading({ kicker, title, copy, light = false, id }) {
  return (
    <div className={`section-heading ${light ? 'section-heading--light' : ''}`}>
      <span className="kicker">{kicker}</span>
      <h2 id={id}>{title}</h2>
      {copy ? <p>{copy}</p> : null}
    </div>
  );
}

const messagePillars = [
  {
    label: 'Sources',
    title: 'Connected evidence',
    text: 'Bring approved advertising, analytics, email, CRM, fundraising, and operations data into one reporting workflow without rebuilding the same spreadsheet every month.',
    color: 'evidence',
  },
  {
    label: 'Meaning',
    title: 'Confidence built in',
    text: 'Organize performance metrics, preserve reporting context, and use clear summaries to understand what changed and what the available evidence supports.',
    color: 'confidence',
  },
  {
    label: 'Audience',
    title: 'Made for the room',
    text: 'Move from operator detail to leadership, board, funder, or client-ready reporting without recreating the explanation from scratch.',
    color: 'room',
  },
];

const audienceGroups = [
  {
    code: 'N',
    type: 'nonprofit',
    title: 'For nonprofits',
    text: 'Bring fundraising and marketing performance into a board- and funder-ready view with the context behind the numbers intact.',
  },
  {
    code: 'A',
    type: 'agency',
    title: 'For agencies',
    text: 'Standardize client reporting across accounts while preserving each client’s goals, channels, and performance story.',
  },
  {
    code: 'I',
    type: 'inhouse',
    title: 'For in-house teams',
    text: 'Connect channel activity to organizational outcomes and give leadership a clear view of what needs attention next.',
  },
];

const confidenceDimensions = [
  { code: 'A', title: 'Source coverage', text: 'Are every active source, account, permission, and reporting window documented and available when the report is due?' },
  { code: 'B', title: 'Conversion integrity', text: 'Do primary outcomes stay separate from convenient signals, duplicate events, and incomplete conversion paths?' },
  { code: 'C', title: 'Metric definitions', text: 'Do teams and stakeholders mean the same thing when they use labels such as revenue, conversion, and return?' },
  { code: 'D', title: 'Outcome reconciliation', text: 'Can platform-attributed results be compared responsibly with the CRM, fundraising, ecommerce, or accounting record?' },
  { code: 'E', title: 'Reporting repeatability', text: 'Can the team produce, review, and deliver the report without a person-dependent monthly rebuild?' },
  { code: 'F', title: 'Decision usefulness', text: 'Does the report make a decision, priority, owner, or next step clearer for the person reading it?' },
];

const integrationGroups = [
  {
    title: 'Advertising',
    text: 'Google Ads, Microsoft Advertising, Meta, LinkedIn, TikTok, X, Nextdoor, and StackAdapt.',
  },
  {
    title: 'Fundraising & CRM',
    text: 'Blackbaud SKY API, Engaging Networks, Fundraise Up, GoFundMe Pro, qgiv, Salsa Labs, Virtuous, and HubSpot.',
  },
  {
    title: 'Email & analytics',
    text: 'Constant Contact, Mailchimp, Klaviyo, Delivra, Matomo, plus supporting spreadsheet context.',
  },
  {
    title: 'Operations',
    text: 'AccuLynx, Billtrust, and the systems that add essential operational context to performance.',
  },
];

const faqItems = [
  {
    question: 'Who is ReportWerks built for?',
    answer: 'ReportWerks is designed for nonprofits, agencies, and in-house marketing teams that must turn connected performance data into clear reporting for boards, funders, clients, and leadership.',
  },
  {
    question: 'Does ReportWerks change campaigns or source accounts?',
    answer: 'Reporting connections are used for reporting, analytics, and user-requested account connection features. They are not used to create, edit, or manage campaigns unless a source-specific workflow explicitly supports and asks the user to authorize that action.',
  },
  {
    question: 'How is AI used?',
    answer: 'AI-assisted workflows help summarize performance, surface patterns, and speed up analysis from campaign and aggregate reporting statistics. They are designed around reporting data rather than personally identifiable information.',
  },
  {
    question: 'Can users disconnect access?',
    answer: 'Yes. Users choose which accounts to connect and can disconnect access inside ReportWerks or revoke it directly from the source platform at any time.',
  },
];

const pricingPlans = [
  {
    id: 'essentials',
    name: 'Essentials',
    monthly: { amount: '100', suffix: '/month' },
    annual: { amount: '1,000', suffix: '/year' },
    capacity: ['1 user', '5 connected data sources'],
    features: [
      'Interactive ReportWerks dashboards',
      'Cross-channel performance reporting',
      'Historical reporting and date comparisons',
      'Automated data updates',
      'Shareable reporting',
      'AI-assisted reporting insights',
      'Standard support',
    ],
    cta: 'Get Started',
  },
  {
    id: 'professional',
    name: 'Professional',
    monthly: { amount: '250', suffix: '/month' },
    annual: { amount: '2,500', suffix: '/year' },
    capacity: ['3 users', '12 connected data sources'],
    features: [
      'Everything in Essentials',
      'Multi-channel reporting',
      'Automated scheduled reports',
      'AI-powered report assistance',
      'Enhanced dashboard configuration',
      'Team reporting access',
      'Priority support',
    ],
    cta: 'Start with Professional',
    popular: true,
  },
  {
    id: 'growth',
    name: 'Growth',
    monthly: { amount: '450', suffix: '/month' },
    annual: { amount: '4,500', suffix: '/year' },
    capacity: ['6 users', '25 connected data sources'],
    features: [
      'Everything in Professional',
      'Expanded cross-channel reporting',
      'Marketing and fundraising performance visibility',
      'Advanced reporting configurations',
      'Automated weekly, monthly, and quarterly reporting',
      'Expanded AI-assisted analysis',
      'Priority onboarding and support',
    ],
    cta: 'Choose Growth',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    capacity: ['10+ users', 'Custom data-source allowances'],
    features: [
      'Everything in Growth',
      'Custom reporting requirements',
      'Specialized integrations',
      'Advanced onboarding',
      'Organization-specific dashboard configuration',
      'Priority implementation and support',
      'Volume pricing',
    ],
    cta: 'Talk to Our Team',
  },
];

const pricingFaqItems = [
  {
    question: 'What is a connected data source?',
    answer: 'A connected data source is an individual account, property, or data connection feeding information into ReportWerks. For example, a Google Analytics property, Google Ads account, or email marketing account may each count as a connected data source.',
  },
  {
    question: 'Can I add additional users?',
    answer: 'Yes. Additional users can be added to Essentials, Professional, and Growth plans for $75 per user per month.',
  },
  {
    question: 'Can I connect additional data sources?',
    answer: 'Yes. Additional standard data sources can be added for $10 per source per month. Specialized or custom integrations may require separate implementation pricing.',
  },
  {
    question: 'Is annual billing available?',
    answer: 'Yes. Annual plans include two months free compared with monthly billing.',
  },
  {
    question: 'Can ReportWerks work with multiple marketing platforms?',
    answer: 'Yes. ReportWerks is designed to bring data from multiple marketing, analytics, advertising, email, fundraising, and other supported platforms into a unified reporting experience.',
  },
  {
    question: "What if our reporting needs don't fit one of these plans?",
    answer: 'Enterprise plans can be tailored for larger teams, higher numbers of data sources, specialized integrations, and custom reporting requirements.',
  },
];

const featuredIntegrationNames = [
  'Google Analytics',
  'Google Ads',
  'Meta',
  'LinkedIn',
  'Microsoft Advertising',
  'Mailchimp',
  'Constant Contact',
  'Klaviyo',
  'TikTok',
  'StackAdapt',
  'Virtuous',
  'Salsa Labs',
];

const featuredIntegrations = featuredIntegrationNames.map((name) =>
  integrations.find((integration) => integration.name === name),
).filter(Boolean);

function getPricingCtaHref(plan, billingPeriod) {
  if (plan.id === 'enterprise') {
    return `/book?plan=${plan.id}&billing=${billingPeriod}`;
  }

  return stripeCheckoutLinks[plan.id][billingPeriod];
}

function trackPricingSelection(plan, billingPeriod, location) {
  const pricing = plan.id === 'enterprise' ? 'custom' : `$${plan[billingPeriod].amount}`;
  const eventContext = {
    plan_name: plan.name,
    billing_period: billingPeriod,
    displayed_price: pricing,
    cta_location: location,
  };

  trackGa4Event(plan.id === 'enterprise' ? 'pricing_enterprise_contact' : 'pricing_plan_selected', eventContext);

  if (plan.id !== 'enterprise') {
    trackGa4Event('begin_checkout', {
      ...eventContext,
      value: Number(plan[billingPeriod].amount.replace(',', '')),
      currency: 'USD',
    });
  }
}

function HomePricingSection() {
  const summaries = [
    { name: 'Essentials', price: 'Starting at $100/month' },
    { name: 'Professional', price: '$250/month', popular: true },
    { name: 'Growth', price: '$450/month' },
    { name: 'Enterprise', price: 'Custom' },
  ];

  return (
    <section className="section home-pricing" aria-labelledby="home-pricing-title">
      <div className="shell">
        <div className="home-pricing-intro">
          <SectionHeading
            kicker="Plans that scale with the reporting room"
            title="Start with the coverage your team needs."
            copy="Clear limits, straightforward expansion pricing, and two months free when you choose annual billing."
            id="home-pricing-title"
          />
          <a className="button" href="/pricing">View Pricing <ArrowIcon /></a>
        </div>
        <div className="home-pricing-grid">
          {summaries.map((plan) => (
            <article className={plan.popular ? 'home-price-card home-price-card--popular' : 'home-price-card'} key={plan.name}>
              <div>
                <h3>{plan.name}</h3>
                {plan.popular ? <span>Most Popular</span> : null}
              </div>
              <p>{plan.price}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingPage() {
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    trackGa4Event('pricing_page_view', {
      billing_period: 'monthly',
      cta_location: 'pricing_page',
      page_location: typeof window !== 'undefined' ? window.location.href : `${canonicalBaseUrl}/pricing`,
      page_title: 'ReportWerks Pricing | Marketing & Fundraising Reporting Dashboard',
    });
  }, []);

  const selectBillingPeriod = (period) => {
    setBillingPeriod(period);
    trackGa4Event('pricing_billing_toggle', {
      billing_period: period,
      cta_location: 'pricing_toggle',
    });
  };

  return (
    <>
      <Header simple />
      <main id="main-content" className="pricing-page">
        <section className="pricing-hero" aria-labelledby="pricing-title">
          <div className="shell pricing-hero-inner">
            <nav className="breadcrumb" aria-label="Breadcrumb"><a href="/">Home</a><span aria-hidden="true">/</span><span>Pricing</span></nav>
            <span className="kicker">ReportWerks plans</span>
            <h1 id="pricing-title">Simple Pricing.<br /><span>Better Reporting.</span></h1>
            <p>Bring your marketing, fundraising, advertising, email, analytics, and performance data together in one place.</p>
            <div className="billing-control-wrap">
              <div className="billing-toggle" role="group" aria-label="Billing period">
                <button type="button" aria-pressed={billingPeriod === 'monthly'} onClick={() => selectBillingPeriod('monthly')}>Monthly</button>
                <button type="button" aria-pressed={billingPeriod === 'annual'} onClick={() => selectBillingPeriod('annual')}>Annual</button>
              </div>
              <span className="annual-savings"><i aria-hidden="true" /> 2 months free with annual billing</span>
            </div>
          </div>
        </section>

        <section className="pricing-plans" aria-label="ReportWerks pricing plans">
          <div className="shell pricing-grid">
            {pricingPlans.map((plan) => {
              const price = plan.id === 'enterprise' ? null : plan[billingPeriod];
              return (
                <article id={plan.id} className={plan.popular ? 'pricing-card pricing-card--popular' : 'pricing-card'} key={plan.id}>
                  {plan.popular ? <span className="popular-label">Most Popular</span> : null}
                  <div className="pricing-card-head">
                    <h2>{plan.name}</h2>
                    {price ? (
                      <p className="plan-price"><span>$</span><strong>{price.amount}</strong><small>{price.suffix}</small></p>
                    ) : (
                      <p className="plan-price plan-price--custom"><strong>Custom</strong><small>pricing</small></p>
                    )}
                    {billingPeriod === 'annual' && price ? <span className="plan-saving">Two months free</span> : null}
                  </div>
                  <ul className="plan-capacity" aria-label={`${plan.name} plan capacity`}>
                    {plan.capacity.map((item) => <li key={item}><CheckIcon /> {item}</li>)}
                  </ul>
                  <a
                    className={plan.popular ? 'button pricing-card-cta' : 'button button--outline pricing-card-cta'}
                    href={getPricingCtaHref(plan, billingPeriod)}
                    onClick={() => trackPricingSelection(plan, billingPeriod, 'pricing_card')}
                  >
                    {plan.cta} <ArrowIcon />
                  </a>
                  {price ? <p className="pricing-checkout-note">Secure checkout powered by Stripe</p> : null}
                  <p className="included-label">What’s included</p>
                  <ul className="plan-features">
                    {plan.features.map((feature) => <li key={feature}><CheckIcon /> <span>{feature}</span></li>)}
                  </ul>
                </article>
              );
            })}
          </div>
        </section>

        <section className="pricing-expansion" aria-labelledby="expansion-title">
          <div className="shell expansion-panel">
            <div><span className="kicker">Build beyond the plan</span><h2 id="expansion-title">Expansion pricing, without guesswork.</h2></div>
            <dl>
              <div><dt>Additional users</dt><dd><strong>$75</strong><span>/user/month</span></dd></div>
              <div><dt>Additional standard connected data sources</dt><dd><strong>$10</strong><span>/source/month</span></dd></div>
            </dl>
            <p>Custom integrations, specialized data sources, and implementation services may be quoted separately.</p>
          </div>
        </section>

        <section className="section pricing-integrations" aria-labelledby="pricing-integrations-title">
          <div className="shell">
            <SectionHeading
              kicker={`${integrationCount} supported connections`}
              title="Integrates with the tools you already use"
              copy="Connect approved accounts across advertising, analytics, email, CRM, fundraising, donation, document, and operations workflows."
              id="pricing-integrations-title"
            />
            <div className="pricing-integration-grid">
              {featuredIntegrations.map((integration) => (
                <div key={integration.name}><span aria-hidden="true">{integration.mark}</span><strong>{integration.name}</strong></div>
              ))}
            </div>
            <a className="text-link" href="/integrations">Explore all {integrationCount} integrations <ArrowIcon /></a>
          </div>
        </section>

        <section className="section pricing-value" aria-labelledby="pricing-value-title">
          <div className="shell">
            <SectionHeading kicker="One reporting layer" title="Less assembly. More confidence." id="pricing-value-title" />
            <div className="pricing-value-grid">
              <article><span>Connected</span><h3>See the full performance picture.</h3><p>Bring channel activity and organizational outcomes into one reporting workflow.</p></article>
              <article><span>Repeatable</span><h3>Stop rebuilding the report.</h3><p>Automate data updates and recurring reporting so your team can focus on what changed.</p></article>
              <article><span>Defensible</span><h3>Keep the context attached.</h3><p>Give leadership, boards, funders, and clients a clear story grounded in connected evidence.</p></article>
            </div>
          </div>
        </section>

        <ComparisonTeaser location="pricing" />
        <section className="section faq pricing-faq" id="faq" aria-labelledby="pricing-faq-title">
          <div className="shell faq-grid">
            <SectionHeading kicker="Pricing questions, answered" title="Know what counts before you choose." id="pricing-faq-title" />
            <div className="faq-list">
              {pricingFaqItems.map((item) => (
                <details key={item.question}>
                  <summary><span>{item.question}</span><span aria-hidden="true">+</span></summary>
                  <div><p>{item.answer}</p></div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="final-cta pricing-final-cta">
          <div className="shell final-cta-inner">
            <div className="final-cta-copy">
              <span className="kicker">Choose with context</span>
              <h2>Find the plan that fits your reporting room.</h2>
              <p>Bring your current data stack and recurring reporting needs. We’ll help you map the right plan and connections.</p>
              <a className="button button--light" href="/book?plan=pricing-conversation" onClick={() => trackGa4Event('pricing_enterprise_contact', { plan_name: 'Not sure', billing_period: billingPeriod, displayed_price: 'custom', cta_location: 'pricing_final_cta' })}>Talk to Our Team <ArrowIcon /></a>
            </div>
            <aside className="pricing-final-note"><strong>Every plan includes</strong><span>Connected dashboards</span><span>Automated data updates</span><span>AI-assisted reporting insights</span></aside>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function HomePage() {
  return (
    <>
      <Header />
      <main id="main-content" itemScope itemType="https://schema.org/SoftwareApplication">
        <meta itemProp="applicationCategory" content="BusinessApplication" />
        <meta itemProp="operatingSystem" content="Web" />
        <section className="hero">
          <div className="shell hero-grid">
            <div className="hero-copy">
              <span className="hero-audience">For nonprofits, agencies, and in-house teams</span>
              <span className="kicker">Confidence before the meeting</span>
              <h1>
                <span className="sr-only" itemProp="name">ReportWerks: </span>
                Reporting you can <span>stand behind.</span>
              </h1>
              <p itemProp="description">
                Connect the data behind performance, preserve the context, and walk into every
                stakeholder conversation with a clear, defensible report.
              </p>
              <div className="button-row">
                <a className="button" href="/marketing-data-confidence-audit-tool">Take the free confidence audit <ArrowIcon /></a>
                <a className="text-link" href="/book">Book a reporting walkthrough <ArrowIcon /></a>
              </div>
              <ul className="hero-proof" aria-label="ReportWerks benefits">
                <li><CheckIcon /> Bring approved reporting sources together</li>
                <li><CheckIcon /> Keep the story behind the numbers intact</li>
              </ul>
            </div>
            <div className="hero-visual">
              <figure className="hero-photo-frame">
                <div className="hero-photo">
                  <img
                    src={heroReportingConfidence}
                    alt="Three reporting leaders reviewing performance evidence before a stakeholder meeting"
                    width="1536"
                    height="1024"
                    fetchPriority="high"
                    decoding="async"
                  />
                  <span className="hero-photo-label">The accountability moment</span>
                </div>
                <figcaption className="hero-report-note">
                  <span><i aria-hidden="true" /> Ready for review</span>
                  <strong>Clear source trail. Clear story.</strong>
                  <small>Reporting built to handle the next question.</small>
                </figcaption>
              </figure>
            </div>
          </div>
          <div className="shell audience-strip" aria-label="Designed for">
            <span>Built for the moment you report to</span>
            <strong>Leadership</strong><i />
            <strong>Boards</strong><i />
            <strong>Funders</strong><i />
            <strong>Clients</strong>
          </div>
        </section>

        <section className="section workflow" id="product">
          <div className="shell">
            <SectionHeading
              kicker="A reporting system, not another dashboard"
              title="Evidence in. Confidence out."
              copy="ReportWerks keeps sources, context, and stakeholder communication connected from the first approved data pull to the conversation that follows."
            />
            <div className="workflow-grid">
              {messagePillars.map((item) => (
                <article className={`workflow-card workflow-card--${item.color}`} key={item.label}>
                  <div className="workflow-card-top"><span>{item.label}</span><b>Report layer</b></div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section audience" id="for-teams">
          <div className="shell audience-grid">
            <div className="audience-photo-wrap">
              <img
                src={reportingTeam}
                alt="A nonprofit and agency team reviewing reporting together around a laptop"
                loading="lazy"
              />
              <div className="photo-caption"><span>The report is only ready when the person presenting it is ready.</span></div>
            </div>
            <div className="audience-copy">
              <span className="kicker">One responsibility, three reporting rooms</span>
              <h2>Built around who needs the explanation next.</h2>
              <p>
                Nonprofit, agency, and in-house teams carry the same burden: turning fragmented
                performance data into an answer another person can understand and trust.
              </p>
              <div className="audience-cards">
                {audienceGroups.map((group) => (
                  <article key={group.title}>
                    <span className={`audience-icon audience-icon--${group.type}`} aria-hidden="true">{group.code}</span>
                    <div><h3>{group.title}</h3><p>{group.text}</p></div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="section confidence" id="confidence">
          <div className="shell">
            <div className="confidence-intro">
              <SectionHeading
                kicker="The Reporting Confidence Framework"
                title="Confidence is built before the report is delivered."
                copy="A practical ReportWerks method for finding risk across the path from connected source data to stakeholder action. It is guidance for stronger reporting—not a certification or an automated guarantee."
              />
              <div className="confidence-action">
                <span>27 checks · 6 reporting risk areas</span>
                <a className="button" href="/marketing-data-confidence-audit-tool">Check your reporting confidence <ArrowIcon /></a>
              </div>
            </div>
            <div className="confidence-grid">
              {confidenceDimensions.map((dimension) => (
                <article className="confidence-card" key={dimension.code}>
                  <div className="confidence-card-meta">
                    <span>Framework {dimension.code}</span>
                    <span>Question to answer</span>
                  </div>
                  <h3>{dimension.title}</h3>
                  <p>{dimension.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section integrations" id="integrations">
          <div className="shell">
            <SectionHeading
              kicker="Connected context"
              title="Connect the reporting sources your team already uses."
              copy="Bring approved data from the platforms doing the work into one organized reporting layer."
              light
            />
            <div className="integration-grid">
              {integrationGroups.map((group, index) => (
                <article key={group.title}>
                  <span className={`integration-dot integration-dot--${index + 1}`} />
                  <h3>{group.title}</h3>
                  <p>{group.text}</p>
                </article>
              ))}
            </div>
            <div className="integration-note">
              <span>{integrationCount} supported connections</span>
              <p>See what each source contributes to a connected reporting workflow.</p>
              <a href="/integrations">Explore every integration <ArrowIcon /></a>
            </div>
          </div>
        </section>

        <section className="section data-section" id="data-usage">
          <div className="shell data-grid">
            <div>
              <SectionHeading
                kicker="Data usage & user control"
                title="What connected platform data ReportWerks accesses and why."
                copy="Clear reporting starts with clear boundaries. Access is scoped to reporting workflows the user requests and approves."
              />
              <div className="data-summary">
                <p><strong>What is accessed:</strong> Authorized reporting fields such as account identifiers, campaign names, spend, traffic, engagement, conversions, donation activity, contacts, invoices, and related performance metrics.</p>
                <p><strong>Why it is used:</strong> To build dashboards, reporting summaries, connection-status views, AI-assisted analysis, and client-ready reports.</p>
                <p><strong>Who controls it:</strong> Users choose what to connect and can disconnect inside ReportWerks or revoke access at the source platform.</p>
              </div>
            </div>
            <aside className="google-card">
              <span className="google-card-label">Google app verification</span>
              <h3>Google API data use</h3>
              <p>When a user connects Google Ads or another Google service, ReportWerks accesses only the authorized reporting data needed for user-requested reporting features.</p>
              <ul>
                <li><CheckIcon /> Used for reporting, dashboards, summaries, connection status, analysis, and client-ready views.</li>
                <li><CheckIcon /> Not used for targeted advertising, retargeting, credit decisions, or sale to data brokers.</li>
                <li><CheckIcon /> Not used for generalized AI model training.</li>
                <li><CheckIcon /> Users can disconnect, revoke access, and request deletion.</li>
              </ul>
              <div className="google-card-links">
                <a href="/privacy-policy">Read the Privacy Policy <ArrowIcon /></a>
                <a href="/terms-of-service">Read the Terms of Service <ArrowIcon /></a>
              </div>
            </aside>
          </div>
        </section>

        <HomePricingSection />
        <ComparisonTeaser location="home" />

        <section className="section faq" id="faq" itemScope itemType="https://schema.org/FAQPage">
          <div className="shell faq-grid">
            <SectionHeading kicker="Questions, answered" title="The essentials before a walkthrough." />
            <div className="faq-list">
              {faqItems.map((item) => (
                <details key={item.question} itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                  <summary><span itemProp="name">{item.question}</span><span aria-hidden="true">+</span></summary>
                  <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p itemProp="text">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="final-cta">
          <div className="shell final-cta-inner">
            <div className="final-cta-copy">
              <span className="kicker">A walkthrough built around your next reporting room</span>
              <h2>Bring the report you have to defend next.</h2>
              <p>Walk through your reporting stack, the people who rely on it, and the questions or points of doubt that return every month.</p>
              <a className="button button--light" href="/book">Book a reporting walkthrough <ArrowIcon /></a>
            </div>
            <aside className="walkthrough-agenda" aria-label="Reporting walkthrough agenda">
              <span className="walkthrough-agenda-label">Walkthrough docket</span>
              <dl>
                <div><dt>01</dt><dd>Your connected source stack</dd></div>
                <div><dt>02</dt><dd>Your recurring reporting gaps</dd></div>
                <div><dt>03</dt><dd>Your stakeholder questions</dd></div>
              </dl>
              <p>No generic product tour. Start with the report that matters now.</p>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

const integrationFaqItems = [
  {
    question: 'What does ReportWerks access from a connected platform?',
    answer: 'Access depends on the platform and the workflow a user authorizes. It can include account identifiers, campaign structure, spend, delivery, engagement, conversions, analytics events, donation activity, contacts, invoices, and related reporting fields.',
  },
  {
    question: 'Does connecting a platform change anything in the source account?',
    answer: 'Reporting connections are used for reporting and user-requested connection workflows. ReportWerks does not create, edit, or manage campaigns unless a source-specific workflow explicitly supports that action and asks the user to authorize it.',
  },
  {
    question: 'Can a connection be removed?',
    answer: 'Yes. Users choose what to connect and can disconnect inside ReportWerks or revoke access directly from the source platform.',
  },
  {
    question: 'What if my reporting source is not listed?',
    answer: 'Bring the source to a reporting walkthrough. We will map the data, reporting outcome, access method, and stakeholder need before recommending a connection path.',
  },
];

function IntegrationsPage() {
  return (
    <>
      <Header simple />
      <main id="main-content" className="connections-page">
        <section className="connections-hero">
          <div className="shell connections-hero-grid">
            <div className="connections-hero-copy">
              <nav className="breadcrumb" aria-label="Breadcrumb">
                <a href="/">Home</a><span aria-hidden="true">/</span><span>Integrations</span>
              </nav>
              <span className="kicker">The ReportWerks connection ledger</span>
              <h1>Every connection should strengthen the report.</h1>
              <p>
                Connect advertising, analytics, CRM, fundraising, email, donation, and document
                workflows in one reporting layer—while keeping the source context behind every number.
              </p>
              <div className="button-row">
                <a className="button" href="#connection-catalog">Explore {integrationCount} connections <ArrowIcon /></a>
                <a className="text-link" href="/book">Talk through your stack <ArrowIcon /></a>
              </div>
            </div>
            <figure className="connections-hero-visual">
              <img
                src="/assets/connected-data-report-ledger.jpg"
                alt="Paper data paths and transparent tracks converging into one organized report"
                width="1536"
                height="1024"
                fetchPriority="high"
                decoding="async"
              />
              <figcaption>
                <span>Connected evidence</span>
                <strong>Source context travels with the story.</strong>
              </figcaption>
            </figure>
          </div>
          <div className="shell connection-totals" aria-label="Integration catalog summary">
            <div><strong>{integrationCount}</strong><span>supported connections</span></div>
            <div><strong>{integrationCategories.length}</strong><span>data families</span></div>
            <div><strong>1</strong><span>reporting layer</span></div>
            <p>Catalog verified against the current ReportWerks application and data API.</p>
          </div>
        </section>

        <section className="section connected-meaning">
          <div className="shell">
            <div className="connected-meaning-intro">
              <SectionHeading
                kicker="What connected data means"
                title="Not a pile of metrics. A traceable reporting workflow."
                copy="A connection matters when your team can understand where the data came from, how it maps to the report, and what the available evidence can support."
              />
              <p className="evidence-note"><span>Evidence note</span> Access varies by platform, account permissions, and the workflow a user approves.</p>
            </div>
            <div className="meaning-grid">
              <article><span>01 / authorize</span><h3>Choose the source</h3><p>Users connect approved accounts through OAuth, a scoped token, credentials, or a supported batch delivery.</p></article>
              <article><span>02 / preserve</span><h3>Keep its context</h3><p>Account mappings, campaign structure, platform definitions, and reporting windows stay attached to the data they explain.</p></article>
              <article><span>03 / report</span><h3>Read outcomes together</h3><p>Channel activity can sit beside analytics, CRM, fundraising, donation, email, and operational outcomes in one recurring workflow.</p></article>
            </div>
          </div>
        </section>

        <section className="section connection-catalog" id="connection-catalog">
          <div className="shell">
            <div className="catalog-heading">
              <SectionHeading
                kicker="Current connections"
                title={`${integrationCount} supported connections. Six kinds of evidence.`}
                copy="Each connection below is represented in the current ReportWerks product catalog and backed by its supported application or data-API workflow."
              />
              <nav className="catalog-index" aria-label="Integration categories">
                {integrationCategories.map((category) => (
                  <a key={category.id} href={`#${category.id}`}><span>{category.code}</span>{category.label}</a>
                ))}
              </nav>
            </div>

            <div className="connection-ledger">
              {integrationCategories.map((category) => {
                const categoryIntegrations = integrations.filter((item) => item.category === category.id);
                return (
                  <section className="ledger-group" id={category.id} key={category.id} aria-labelledby={`${category.id}-title`}>
                    <aside className="ledger-summary">
                      <span className="ledger-code">{category.code}</span>
                      <span className="ledger-count">{categoryIntegrations.length} {categoryIntegrations.length === 1 ? 'connection' : 'connections'}</span>
                      <h2 id={`${category.id}-title`}>{category.label}</h2>
                      <p>{category.summary}</p>
                      <div className="ledger-data-note"><strong>Connected data</strong><span>{category.connectedMeaning}</span></div>
                    </aside>
                    <div className="connector-list">
                      {categoryIntegrations.map((integration) => (
                        <article className="connector-card" key={integration.name}>
                          <div className="connector-card-head">
                            <span className="connector-mark" aria-hidden="true">{integration.mark}</span>
                            <div><h3>{integration.name}</h3><span>{integration.auth}</span></div>
                          </div>
                          <p>{integration.detail}</p>
                        </article>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        </section>

        <section className="section connection-path">
          <div className="shell connection-path-grid">
            <div>
              <span className="kicker">From permission to presentation</span>
              <h2>Connected does not mean context-free.</h2>
              <p>ReportWerks keeps the reporting path explicit so a stakeholder-ready view can still be traced back to the systems and choices behind it.</p>
            </div>
            <ol>
              <li><span>01</span><div><strong>Authorize</strong><p>The user chooses a source and grants the access needed for the requested workflow.</p></div></li>
              <li><span>02</span><div><strong>Map</strong><p>The selected account, property, site, advertiser, or organization is tied to the correct client profile.</p></div></li>
              <li><span>03</span><div><strong>Interpret</strong><p>Metrics retain the platform and reporting context needed to explain what they represent.</p></div></li>
              <li><span>04</span><div><strong>Present</strong><p>The connected evidence supports dashboards, summaries, analysis, and client-ready reports.</p></div></li>
            </ol>
          </div>
        </section>

        <ComparisonTeaser location="integrations" />
        <section className="section connection-control">
          <div className="shell connection-control-grid">
            <div>
              <span className="kicker">Access with boundaries</span>
              <h2>Your connections stay under your control.</h2>
            </div>
            <div className="control-statements">
              <p><strong>Purpose-limited access.</strong> Connected data is used for user-requested reporting, dashboards, summaries, connection status, analysis, and report-ready views.</p>
              <p><strong>User-controlled connections.</strong> Users choose which accounts to connect and can disconnect in ReportWerks or revoke access at the source.</p>
              <p><strong>Clear AI boundaries.</strong> Connected platform data is not used for generalized AI model training.</p>
              <div><a href="/privacy-policy">Read the Privacy Policy <ArrowIcon /></a><a href="/terms-of-service">Read the Terms of Service <ArrowIcon /></a></div>
            </div>
          </div>
        </section>

        <section className="section integrations-faq" itemScope itemType="https://schema.org/FAQPage">
          <div className="shell faq-grid">
            <SectionHeading kicker="Connection questions" title="Know what the connection is for." />
            <div className="faq-list">
              {integrationFaqItems.map((item) => (
                <details key={item.question} itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                  <summary><span itemProp="name">{item.question}</span><span aria-hidden="true">+</span></summary>
                  <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer"><p itemProp="text">{item.answer}</p></div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="final-cta connections-cta">
          <div className="shell final-cta-inner">
            <div className="final-cta-copy">
              <span className="kicker">Bring your source list</span>
              <h2>See what your stack can explain together.</h2>
              <p>Walk through the sources you use, the report you produce, and the questions your stakeholders ask next.</p>
              <a className="button button--light" href="/book">Book a reporting walkthrough <ArrowIcon /></a>
            </div>
            <aside className="walkthrough-agenda" aria-label="Integration walkthrough agenda">
              <span className="walkthrough-agenda-label">Connection review</span>
              <dl>
                <div><dt>01</dt><dd>Source coverage</dd></div>
                <div><dt>02</dt><dd>Account and outcome mapping</dd></div>
                <div><dt>03</dt><dd>Stakeholder-ready reporting path</dd></div>
              </dl>
              <p>No generic demo first. Start with the reporting stack you actually have.</p>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function AppointmentForm() {
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState({ type: 'idle', message: '' });

  const handleFieldChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const validateForm = () => {
    const next = {};
    if (!formData.name.trim()) next.name = 'Enter your full name.';
    if (!formData.email.trim()) next.email = 'Enter your work email.';
    else if (!emailPattern.test(formData.email.trim())) next.email = 'Enter a valid email address.';
    if (!formData.organization.trim()) next.organization = 'Enter your organization name.';
    if (!formData.preferred_window.trim()) next.preferred_window = 'Share a time window that works for you.';
    if (!formData.timeline.trim()) next.timeline = 'Choose your current timeline.';
    if (!formData.biggest_challenge.trim()) next.biggest_challenge = 'Tell us what is breaking down in reporting today.';
    if (!formData.appointment_goal.trim()) next.appointment_goal = 'Tell us what you want from the appointment.';
    if (!formData.consent) next.consent = 'Confirm that we can follow up about your request.';
    return next;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const submittedForm = new FormData(event.currentTarget);
    if (String(submittedForm.get('company') || '').trim()) {
      setSubmitState({ type: 'error', message: 'Submission was blocked. Please refresh the page and try again.' });
      return;
    }
    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setSubmitState({ type: 'error', message: 'Please fix the highlighted fields and try again.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitState({ type: 'idle', message: '' });
    const messageLines = [
      `Organization: ${formData.organization}`,
      formData.website ? `Website: ${formData.website}` : '',
      formData.role ? `Role: ${formData.role}` : '',
      formData.organization_type ? `Organization type: ${formData.organization_type}` : '',
      formData.team_size ? `Team size: ${formData.team_size}` : '',
      formData.phone ? `Phone: ${formData.phone}` : '',
      `Preferred appointment window: ${formData.preferred_window}`,
      `Timeline: ${formData.timeline}`,
      formData.reporting_stack ? `Current reporting stack: ${formData.reporting_stack}` : '',
      `Biggest reporting challenge: ${formData.biggest_challenge}`,
      `Appointment goal: ${formData.appointment_goal}`,
      `Consent to follow up: ${formData.consent ? 'Yes' : 'No'}`,
    ].filter(Boolean);
    const payload = {
      ...formData,
      domain: String(submittedForm.get('domain') || hostname).trim(),
      goal: 'ReportWerks appointment request',
      message: messageLines.join('\n'),
      consent: formData.consent ? 'Yes' : 'No',
      source: 'ReportWerks booking page form',
      submitted_at: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      emailid: 27,
      responder: 26,
      segment: 26,
      emailContactId: 190789,
    };

    try {
      await submitMauticLead(appointmentEndpoint, payload, 'Unable to submit your request right now.');
      trackLeadFormSubmission({
        formContext: buildBookingLeadTrackingContext(),
        includeGenerateLead: true,
        includeCloseConvertLead: true,
      });
      setFormData(initialFormState);
      setErrors({});
      setSubmitState({ type: 'success', message: successMessage });
    } catch (error) {
      setSubmitState({ type: 'error', message: error.message || 'Something went wrong. Please try again in a moment.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const common = { fullWidth: true, size: 'small', variant: 'outlined', onChange: handleFieldChange };

  return (
    <div className="booking-form-card" id="booking-form">
      <span className="kicker">Direct-to-inbox request</span>
      <h2>Request your appointment</h2>
      <p>Tell us what is slowing reporting down and when you are available. Donnie will reply with a focused next step.</p>
      {submitState.type !== 'idle' ? <Alert severity={submitState.type === 'success' ? 'success' : 'error'}>{submitState.message}</Alert> : null}
      {submitState.type === 'success' ? null : (
        <form noValidate onSubmit={handleSubmit}>
          <input type="hidden" name="domain" value={formData.domain} readOnly />
          <div className="form-grid">
            <TextField {...common} required label="Full name" name="name" value={formData.name} error={Boolean(errors.name)} helperText={errors.name} />
            <TextField {...common} required type="email" label="Work email" name="email" value={formData.email} error={Boolean(errors.email)} helperText={errors.email} />
            <TextField {...common} required label="Organization" name="organization" value={formData.organization} error={Boolean(errors.organization)} helperText={errors.organization} />
            <TextField {...common} label="Website" name="website" value={formData.website} placeholder="https://" />
            <TextField {...common} label="Role" name="role" value={formData.role} />
            <TextField {...common} type="tel" label="Phone" name="phone" value={formData.phone} />
            <TextField {...common} select label="Organization type" name="organization_type" value={formData.organization_type}>
              <MenuItem value="">Select one</MenuItem><MenuItem value="Nonprofit">Nonprofit</MenuItem><MenuItem value="Agency">Agency</MenuItem><MenuItem value="In-house marketing team">In-house marketing team</MenuItem><MenuItem value="Consultant">Consultant</MenuItem><MenuItem value="Other">Other</MenuItem>
            </TextField>
            <TextField {...common} select label="Team size" name="team_size" value={formData.team_size}>
              <MenuItem value="">Select one</MenuItem><MenuItem value="1-5">1-5</MenuItem><MenuItem value="6-15">6-15</MenuItem><MenuItem value="16-50">16-50</MenuItem><MenuItem value="51+">51+</MenuItem>
            </TextField>
          </div>
          <TextField {...common} required label="Preferred appointment window" name="preferred_window" value={formData.preferred_window} placeholder="Tuesday after 2pm ET, Thursday morning, etc." error={Boolean(errors.preferred_window)} helperText={errors.preferred_window} />
          <TextField {...common} required select label="Timeline" name="timeline" value={formData.timeline} error={Boolean(errors.timeline)} helperText={errors.timeline}>
            <MenuItem value="">Select one</MenuItem><MenuItem value="Need help this week">Need help this week</MenuItem><MenuItem value="Need help this month">Need help this month</MenuItem><MenuItem value="This quarter">This quarter</MenuItem><MenuItem value="Just exploring">Just exploring</MenuItem>
          </TextField>
          <TextField {...common} multiline minRows={3} label="Current reporting stack" name="reporting_stack" value={formData.reporting_stack} placeholder="Meta, Google Ads, GA4, HubSpot, spreadsheets, fundraising tools, etc." />
          <TextField {...common} required multiline minRows={4} label="Biggest reporting challenge" name="biggest_challenge" value={formData.biggest_challenge} placeholder="What is breaking, taking too long, or creating confusion?" error={Boolean(errors.biggest_challenge)} helperText={errors.biggest_challenge} />
          <TextField {...common} required multiline minRows={4} label="What do you want from the appointment?" name="appointment_goal" value={formData.appointment_goal} placeholder="Evaluate fit, scope a rollout, improve board or client reporting, etc." error={Boolean(errors.appointment_goal)} helperText={errors.appointment_goal} />
          <input aria-hidden="true" autoComplete="new-password" name="company" className="honeypot" tabIndex="-1" type="text" />
          <FormControlLabel
            control={<Checkbox name="consent" checked={formData.consent} onChange={handleFieldChange} />}
            label="You can contact me about this appointment request."
          />
          {errors.consent ? <span className="form-error">{errors.consent}</span> : null}
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <><CircularProgress color="inherit" size={18} /> Sending request</> : 'Request My Appointment'}
          </Button>
          <small>Prefer email? Contact <a href={supportEmailHref}>{supportEmail}</a>.</small>
        </form>
      )}
    </div>
  );
}

function AuditDownloadForm() {
  const [formData, setFormData] = useState({ firstName: '', email: '', consent: false });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState({ type: 'idle', message: '' });

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const submittedForm = new FormData(event.currentTarget);
    if (String(submittedForm.get('company') || '').trim()) return;
    const nextErrors = {};
    if (!formData.firstName.trim()) nextErrors.firstName = 'Enter your first name.';
    if (!formData.email.trim()) nextErrors.email = 'Enter your work email.';
    else if (!emailPattern.test(formData.email.trim())) nextErrors.email = 'Enter a valid email address.';
    if (!formData.consent) nextErrors.consent = 'Confirm that we can email the audit to you.';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setSubmitState({ type: 'error', message: 'Please fix the highlighted fields.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitState({ type: 'idle', message: '' });
    const payload = {
      name: formData.firstName.trim(),
      firstname: formData.firstName.trim(),
      email: formData.email.trim(),
      consent: 'Yes',
      domain: hostname,
      goal: 'Marketing Data Confidence Audit download',
      message: 'Requested the ReportWerks Marketing Data Confidence Audit PDF.',
      source: 'ReportWerks Marketing Data Confidence Audit landing page',
      submitted_at: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      emailid: auditNotificationId,
      emailContactId: 190789,
      responder: auditResponderId,
      segment: auditSegmentId,
    };

    try {
      const result = await submitMauticLead(auditLeadEndpoint, payload, 'Unable to send the audit right now.');
      grantAuditAccess(result.auditAccess);
      trackLeadFormSubmission({
        formContext: {
          form_id: 'reportwerks-marketing-data-confidence-audit-form',
          form_name: 'Marketing Data Confidence Audit download',
          form_location: '/marketing-data-confidence-audit',
          lead_type: 'lead_magnet_download',
          page_location: typeof window !== 'undefined' ? window.location.href : `${canonicalBaseUrl}/marketing-data-confidence-audit`,
          page_title: typeof document !== 'undefined' ? document.title : 'Marketing Data Confidence Audit',
          source_component: 'lead_magnet_form',
        },
        includeSignUp: true,
        includeCloseConvertLead: false,
      });
      setFormData({ firstName: '', email: '', consent: false });
      setErrors({});
      setSubmitState({ type: 'success', message: 'Your secure PDF link is on the way. Your interactive audit is unlocked and ready to begin.' });
    } catch (error) {
      setSubmitState({ type: 'error', message: error.message || 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitState.type === 'success') {
    return (
      <div className="audit-form-card audit-form-success" id="download">
        <span className="audit-form-check" aria-hidden="true"><CheckIcon /></span>
        <span className="kicker">Access unlocked</span>
        <h2>Your online audit is ready.</h2>
        <p>{submitState.message}</p>
        <a className="button" href={auditToolPath}>Start the online audit <ArrowIcon /></a>
        <AuditDownloadButton className="button">Download the audit PDF</AuditDownloadButton>
        <small>A secure download link is also in your email. Start online for an instant score and prioritized next steps.</small>
      </div>
    );
  }

  return (
    <div className="audit-form-card" id="download">
      <span className="kicker">Free PDF scorecard</span>
      <h2>Get the 30-minute audit.</h2>
      <p>Enter your first name and work email. We’ll send the PDF directly to your inbox.</p>
      {submitState.type === 'error' ? <Alert severity="error">{submitState.message}</Alert> : null}
      <form noValidate onSubmit={handleSubmit}>
        <div className="audit-field">
          <label htmlFor="audit-first-name">First name</label>
          <input id="audit-first-name" name="firstName" type="text" autoComplete="given-name" value={formData.firstName} onChange={handleChange} aria-invalid={Boolean(errors.firstName)} aria-describedby={errors.firstName ? 'audit-first-name-error' : undefined} />
          {errors.firstName ? <span className="form-error" id="audit-first-name-error">{errors.firstName}</span> : null}
        </div>
        <div className="audit-field">
          <label htmlFor="audit-email">Work email</label>
          <input id="audit-email" name="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'audit-email-error' : undefined} />
          {errors.email ? <span className="form-error" id="audit-email-error">{errors.email}</span> : null}
        </div>
        <input aria-hidden="true" autoComplete="new-password" name="company" className="honeypot" tabIndex="-1" type="text" />
        <label className="audit-consent">
          <input name="consent" type="checkbox" checked={formData.consent} onChange={handleChange} />
          <span>Send me the audit and practical reporting guidance. I can unsubscribe anytime.</span>
        </label>
        {errors.consent ? <span className="form-error">{errors.consent}</span> : null}
        <button className="button audit-submit" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Preparing your audit…' : 'Send me the audit'}
        </button>
        <small>We’ll use your information to deliver the resource. Read our <a href="/privacy-policy">Privacy Policy</a>.</small>
      </form>
    </div>
  );
}

function LeadMagnetPage() {
  const auditAreas = [
    'Connection and source coverage',
    'Conversion integrity and deduplication',
    'Metric definitions and channel consistency',
    'Revenue reconciliation and attribution',
    'Recurring reporting workflow',
    'Executive usefulness and accountability',
  ];

  return (
    <div className="lead-magnet-page">
      <header className="lead-magnet-header">
        <div className="shell lead-magnet-nav">
          <Logo />
          <a href="#download">Get the free audit <ArrowIcon /></a>
        </div>
      </header>
      <main id="main-content">
        <section className="audit-hero">
          <div className="audit-orb audit-orb--one" aria-hidden="true" />
          <div className="audit-orb audit-orb--two" aria-hidden="true" />
          <div className="shell audit-hero-grid">
            <div className="audit-hero-copy">
              <span className="kicker">Free 30-minute assessment</span>
              <h1>Can you trust your marketing reports?</h1>
              <p>Find the gaps behind conflicting numbers, inflated conversions, slow reporting, and decisions no one can confidently defend.</p>
              <div className="audit-proof" aria-label="Audit overview">
                <span><strong>27</strong> checks</span>
                <span><strong>6</strong> risk areas</span>
                <span><strong>1</strong> action plan</span>
              </div>
              <div className="audit-tool-promo">
                <a href="/marketing-data-confidence-audit-tool">Take the interactive audit <ArrowIcon /></a>
                <span>Name and email unlock your instant score.</span>
              </div>
            </div>
            <AuditDownloadForm />
          </div>
        </section>

        <section className="audit-value">
          <div className="shell">
            <div className="audit-section-heading">
              <span className="kicker">A useful diagnostic</span>
              <h2>Score the reporting system behind the dashboard.</h2>
              <p>This is not another generic ebook. Use the audit to find weak points, compare risk areas, and choose the three improvements that matter most.</p>
            </div>
            <div className="audit-benefit-grid">
              <article><span>01</span><h3>Find hidden blind spots</h3><p>Check whether active platforms, permissions, date ranges, and reporting sources are complete and stable.</p></article>
              <article><span>02</span><h3>Test conversion confidence</h3><p>Separate real outcomes from microconversions, duplicate events, and revenue values that do not reconcile.</p></article>
              <article><span>03</span><h3>Leave with an action plan</h3><p>Turn your score into three owned priorities and a practical sequence for the next 30 days.</p></article>
            </div>
          </div>
        </section>

        <section className="audit-inside">
          <div className="shell audit-inside-grid">
            <div className="audit-cover-stage">
              <div className="audit-cover-shadow" aria-hidden="true" />
              <img src={auditCover} alt="Cover of the ReportWerks Marketing Data Confidence Audit" width="1105" height="1430" />
              <span className="audit-cover-note">12-page practical scorecard</span>
            </div>
            <div className="audit-inside-copy">
              <span className="kicker">What’s inside</span>
              <h2>Audit the full path from platform data to business decision.</h2>
              <p>Score each statement as yes, partly, or no. Your totals reveal where reporting is trusted, usable but vulnerable, or creating decision risk.</p>
              <ul>
                {auditAreas.map((area) => <li key={area}><CheckIcon /> {area}</li>)}
              </ul>
              <blockquote>A polished dashboard cannot compensate for missing accounts, unreliable conversion signals, or conflicting definitions.</blockquote>
            </div>
          </div>
        </section>

        <section className="audit-final-cta">
          <div className="shell">
            <span className="kicker">Make the next report easier to trust</span>
            <h2>Know where your reporting process stands.</h2>
            <p>Complete the audit in 30 minutes and leave with a prioritized plan.</p>
            <a className="button" href="#download">Get the free PDF <ArrowIcon /></a>
          </div>
        </section>
      </main>
      <footer className="lead-magnet-footer">
        <div className="shell"><span>© {new Date().getFullYear()} ReportWerks by DigitalWerks</span><a href="/privacy-policy">Privacy Policy</a></div>
      </footer>
    </div>
  );
}

function BookingPage() {
  return (
    <>
      <Header simple />
      <main id="main-content" className="booking-page" itemScope itemType="https://schema.org/ContactPage">
        <section className="booking-hero">
          <div className="shell booking-grid">
            <div className="booking-copy">
              <span className="kicker">A reporting conversation with a point</span>
              <h1 itemProp="name">Show us where reporting gets stuck.</h1>
              <p>Bring your current stack, the audience you report to, and the work that keeps repeating. We’ll focus the conversation on a practical next step.</p>
              <ul>
                <li><CheckIcon /> Map the sources feeding your reports today.</li>
                <li><CheckIcon /> Identify the exports, screenshots, and rebuilds creating drag.</li>
                <li><CheckIcon /> Clarify what leadership, boards, funders, or clients need to see.</li>
              </ul>
              <div className="booking-aside">
                <strong>What happens next?</strong>
                <p>Your request goes directly to Donnie. He’ll review the context and reply with scheduling options and any useful follow-up questions.</p>
              </div>
            </div>
            <AppointmentForm />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function LegalPage({ content }) {
  return (
    <>
      <Header simple />
      <main id="main-content" className="legal-page" itemScope itemType="https://schema.org/WebPage">
        <div className="shell legal-shell">
          <header className="legal-header">
            <span className="kicker">{content.eyebrow}</span>
            <h1 itemProp="name">{content.title}</h1>
            <p itemProp="description">{content.intro}</p>
            <span>Last updated <time itemProp="dateModified">{content.lastUpdated}</time></span>
          </header>
          <div className="legal-content">
            {content.sections.map((section) => (
              <section key={section.heading}>
                <h2>{section.heading}</h2>
                {section.blocks.map((block, index) => (
                  <div className="legal-block" key={`${section.heading}-${index}`}>
                    {block.title ? <h3>{block.title}</h3> : null}
                    {block.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                    {block.bullets ? <ul>{block.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul> : null}
                  </div>
                ))}
              </section>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function NotFoundPage() {
  return (
    <>
      <Header simple />
      <main id="main-content" className="not-found">
        <div className="shell">
          <span className="kicker">404 · page not found</span>
          <h1>This page slipped out of the report.</h1>
          <p>Return to the product overview or book a walkthrough if you were looking for the next step.</p>
          <div className="button-row"><a className="button" href="/">Go to the homepage</a><a className="text-link" href="/book">Book a walkthrough <ArrowIcon /></a></div>
        </div>
      </main>
      <Footer />
    </>
  );
}

const organizationSchema = {
  '@type': 'Organization',
  '@id': `${canonicalBaseUrl}/#organization`,
  name: 'ReportWerks',
  url: `${canonicalBaseUrl}/`,
  logo: {
    '@type': 'ImageObject',
    url: `${canonicalBaseUrl}/android-chrome-512x512.png`,
    width: 512,
    height: 512,
  },
  email: supportEmail,
  contactPoint: {
    '@type': 'ContactPoint',
    email: supportEmail,
    contactType: 'customer support',
    availableLanguage: 'English',
  },
};

const websiteSchema = {
  '@type': 'WebSite',
  '@id': `${canonicalBaseUrl}/#website`,
  url: `${canonicalBaseUrl}/`,
  name: 'ReportWerks',
  description: 'Reporting confidence software for nonprofit, agency, and marketing teams accountable to boards, funders, clients, and leadership.',
  publisher: { '@id': `${canonicalBaseUrl}/#organization` },
  inLanguage: 'en-US',
};

function buildStructuredData(page, canonical) {
  const graph = [organizationSchema, websiteSchema];
  if (page === 'home') {
    graph.push(
      {
        '@type': 'SoftwareApplication',
        '@id': `${canonicalBaseUrl}/#software`,
        name: 'ReportWerks',
        url: `${canonicalBaseUrl}/`,
        description: 'Reporting software that connects marketing, fundraising, analytics, CRM, email, and operations data into clear, defensible reports for nonprofit, agency, and marketing teams.',
        applicationCategory: 'BusinessApplication',
        applicationSubCategory: 'Reporting and analytics software',
        operatingSystem: 'Web',
        browserRequirements: 'Requires a modern web browser',
        audience: [
          { '@type': 'Audience', audienceType: 'Nonprofit teams' },
          { '@type': 'Audience', audienceType: 'Agencies' },
          { '@type': 'Audience', audienceType: 'Marketing teams' },
        ],
        featureList: [
          'Connected reporting data sources',
          'Reporting dashboards',
          'AI-assisted performance analysis',
          'Stakeholder-ready reports',
        ],
        provider: { '@id': `${canonicalBaseUrl}/#organization` },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonicalBaseUrl}/#faq`,
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    );
  } else if (page !== 'not-found') {
    const pageNames = {
      book: 'Book a ReportWerks Walkthrough',
      integrations: 'ReportWerks Integrations',
      pricing: 'ReportWerks Pricing',
      comparison: 'ReportWerks vs. Google Looker Studio',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      audit: 'Marketing Data Confidence Audit',
      'audit-tool': 'Marketing Data Confidence Audit Tool',
    };
    graph.push(
      {
        '@type': page === 'book' ? 'ContactPage' : page === 'integrations' ? 'CollectionPage' : page === 'pricing' ? 'CollectionPage' : 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: pageNames[page],
        isPartOf: { '@id': `${canonicalBaseUrl}/#website` },
        about: { '@id': `${canonicalBaseUrl}/#organization` },
        inLanguage: 'en-US',
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${canonicalBaseUrl}/` },
          { '@type': 'ListItem', position: 2, name: pageNames[page], item: canonical },
        ],
      },
    );
    if (page === 'comparison') {
      graph.push({
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: comparisonFaqs.map((item) => ({
          '@type': 'Question', name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      });
    }
    if (page === 'audit-tool') {
      graph.push({
        '@type': 'WebApplication',
        '@id': `${canonical}#application`,
        name: 'Marketing Data Confidence Audit',
        url: canonical,
        description: 'A free interactive 27-check assessment for evaluating marketing reporting data, conversions, attribution, workflow, and executive usefulness.',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        browserRequirements: 'Requires a modern web browser',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        provider: { '@id': `${canonicalBaseUrl}/#organization` },
      });
    }
    if (page === 'integrations') {
      graph.push({
        '@type': 'ItemList',
        '@id': `${canonical}#catalog`,
        name: 'ReportWerks supported integrations',
        numberOfItems: integrationCount,
        itemListElement: integrations.map((integration, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: integration.name,
        })),
      });
      graph.push({
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: integrationFaqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      });
    }
    if (page === 'pricing') {
      graph.push({
        '@type': 'SoftwareApplication',
        '@id': `${canonical}#software`,
        name: 'ReportWerks',
        url: canonical,
        description: 'Unified marketing, analytics, advertising, email, fundraising, and performance reporting software.',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        provider: { '@id': `${canonicalBaseUrl}/#organization` },
        offers: pricingPlans.flatMap((plan) => {
          if (plan.id === 'enterprise') return [];
          return [
            {
              '@type': 'Offer',
              name: `${plan.name} monthly`,
              price: plan.monthly.amount.replace(',', ''),
              priceCurrency: 'USD',
              url: `${canonical}#${plan.id}`,
              priceSpecification: { '@type': 'UnitPriceSpecification', price: plan.monthly.amount.replace(',', ''), priceCurrency: 'USD', unitText: 'MONTH' },
            },
            {
              '@type': 'Offer',
              name: `${plan.name} annual`,
              price: plan.annual.amount.replace(',', ''),
              priceCurrency: 'USD',
              url: `${canonical}#${plan.id}`,
              priceSpecification: { '@type': 'UnitPriceSpecification', price: plan.annual.amount.replace(',', ''), priceCurrency: 'USD', unitText: 'YEAR' },
            },
          ];
        }),
      });
      graph.push({
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: pricingFaqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      });
    }
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

function setMetaTag(selector, attributes) {
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
}

function updateDocumentMetadata(page) {
  const metadata = {
    comparison: { ...comparisonMetadata, canonical: `${canonicalBaseUrl}${comparisonPath}` },
    home: {
      title: 'Reporting You Can Stand Behind | ReportWerks',
      description: 'Reporting software for nonprofits, agencies, and marketing teams that turns connected performance data into clear, defensible reports for every stakeholder.',
      canonical: `${canonicalBaseUrl}/`,
    },
    book: {
      title: 'Book a ReportWerks Walkthrough',
      description: 'Book a focused ReportWerks walkthrough for your reporting stack, workflow, and stakeholder needs.',
      canonical: `${canonicalBaseUrl}/book`,
    },
    integrations: {
      title: 'Marketing, CRM & Fundraising Integrations | ReportWerks',
      description: `Explore ${integrationCount} ReportWerks integrations for advertising, analytics, CRM, fundraising, email, donation, and document reporting.`,
      canonical: `${canonicalBaseUrl}/integrations`,
      image: `${canonicalBaseUrl}/assets/connected-data-report-ledger.jpg`,
    },
    pricing: {
      title: 'ReportWerks Pricing | Marketing & Fundraising Reporting Dashboard',
      description: 'Explore ReportWerks pricing for unified marketing, analytics, advertising, email and fundraising reporting. Plans start at $100 per month.',
      canonical: `${canonicalBaseUrl}/pricing`,
    },
    privacy: {
      title: 'Privacy Policy | ReportWerks',
      description: 'Read the ReportWerks Privacy Policy covering connected integrations, Google API data, and AI-assisted reporting.',
      canonical: `${canonicalBaseUrl}/privacy-policy`,
    },
    terms: {
      title: 'Terms of Service | ReportWerks',
      description: 'Read the ReportWerks Terms of Service for website use, connected accounts, and reporting features.',
      canonical: `${canonicalBaseUrl}/terms-of-service`,
    },
    audit: {
      title: 'Free Marketing Data Confidence Audit | ReportWerks',
      description: 'Score your marketing reporting process in 30 minutes with a free 27-point audit covering data quality, conversions, attribution, and workflow.',
      canonical: `${canonicalBaseUrl}/marketing-data-confidence-audit`,
      image: `${canonicalBaseUrl}/android-chrome-512x512.png`,
    },
    'audit-tool': {
      title: 'Free Marketing Data Confidence Audit Tool | ReportWerks',
      description: 'Answer 27 reporting checks and get an instant marketing data confidence score, risk-area breakdown, three priority fixes, and reporting-tax estimate.',
      canonical: `${canonicalBaseUrl}/marketing-data-confidence-audit-tool`,
      image: `${canonicalBaseUrl}/assets/ad-creatives/reportwerks-audit-corner-notification.png`,
    },
    'not-found': {
      title: 'Page Not Found | ReportWerks',
      description: 'The requested ReportWerks page was not found.',
      canonical: `${canonicalBaseUrl}/404`,
    },
  }[page];
  if (page === 'not-found' && typeof window !== 'undefined') {
    metadata.canonical = `${canonicalBaseUrl}${window.location.pathname}`;
  }
  document.title = metadata.title;
  setMetaTag('meta[name="description"]', { name: 'description', content: metadata.description });
  setMetaTag('meta[name="robots"]', {
    name: 'robots',
    content: page === 'not-found' ? 'noindex,follow' : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
  });
  setMetaTag('meta[property="og:title"]', { property: 'og:title', content: metadata.title });
  setMetaTag('meta[property="og:description"]', { property: 'og:description', content: metadata.description });
  setMetaTag('meta[property="og:url"]', { property: 'og:url', content: metadata.canonical });
  setMetaTag('meta[property="og:type"]', { property: 'og:type', content: 'website' });
  setMetaTag('meta[property="og:image"]', { property: 'og:image', content: metadata.image || `${canonicalBaseUrl}/android-chrome-512x512.png` });
  setMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: metadata.title });
  setMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: metadata.description });
  setMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: metadata.image || `${canonicalBaseUrl}/android-chrome-512x512.png` });
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', metadata.canonical);
  let structuredData = document.querySelector('#reportwerks-structured-data');
  if (!structuredData) {
    structuredData = document.createElement('script');
    structuredData.id = 'reportwerks-structured-data';
    structuredData.type = 'application/ld+json';
    document.head.appendChild(structuredData);
  }
  structuredData.textContent = JSON.stringify(buildStructuredData(page, metadata.canonical));
}

function App() {
  const currentPage = getCurrentPage();
  useEffect(() => updateDocumentMetadata(currentPage), [currentPage]);
  return (
    <div className="app-shell" id="top">
      <a className="skip-link" href="#main-content">Skip to content</a>
      {currentPage === 'book' ? <BookingPage /> : null}
      {currentPage === 'integrations' ? <IntegrationsPage /> : null}
      {currentPage === 'pricing' ? <PricingPage /> : null}
      {currentPage === 'comparison' ? <><Header simple /><ComparisonPage SectionHeading={SectionHeading} /><Footer /></> : null}
      {currentPage === 'privacy' ? <LegalPage content={privacyPolicyContent} /> : null}
      {currentPage === 'terms' ? <LegalPage content={termsOfServiceContent} /> : null}
      {currentPage === 'audit' ? <LeadMagnetPage /> : null}
      {currentPage === 'audit-tool' ? <MarketingDataAuditTool /> : null}
      {currentPage === 'not-found' ? <NotFoundPage /> : null}
      {currentPage === 'home' ? <HomePage /> : null}
    </div>
  );
}

export default App;
